import React, { useState, useEffect } from "react";
import axios from "axios";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Spinner,
    Input,
    InputGroup,
    InputGroupText,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Label,
    FormGroup
} from "reactstrap";
import { useAuth } from "../../Provider/AuthProvider";
import { useNavigate } from "react-router-dom";

// Interface d'un entrepôt (identique à Stock)
interface Entrepot {
    id: string;
    ref: string;
}

interface SupplierOrderLine {
    supplier_order_line_id: number;
    supplier_order: {
        id: number;
        ref: string;
        date: string;
    };
    supplier: {
        id: number;      // nécessaire pour setreception
        nom: string;
    };
    product: {
        id: number;
        ref: string;
        label: string;
    };
    line_details: {
        description: string;
        subprice: number;
        total_ht: number;
        total_tva: number;
        total_ttc: number;
    };
    quantities: {
        ordered: number;
        received: number;
        remaining: number;
    };
    statut: number;
}

interface ReceiveModalProps {
    isOpen: boolean;
    toggle: () => void;
    orderLine: SupplierOrderLine | null;
    onReceive: (orderLine: SupplierOrderLine, quantity: number, warehouseId: string) => Promise<void>;
    warehouses: Entrepot[];
    selectedWarehouseId: string;
    onWarehouseChange: (id: string) => void;
}

const ReceiveModal: React.FC<ReceiveModalProps> = ({
    isOpen,
    toggle,
    orderLine,
    onReceive,
    warehouses,
    selectedWarehouseId,
    onWarehouseChange
}) => {
    const [quantity, setQuantity] = useState<number>(0);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (orderLine) {
            setQuantity(orderLine.quantities.remaining);
        }
    }, [orderLine]);

    const handleSubmit = async () => {
        if (!orderLine) return;
        if (!selectedWarehouseId) {
            alert("Veuillez sélectionner un entrepôt");
            return;
        }
        setLoading(true);
        try {
            await onReceive(orderLine, quantity, selectedWarehouseId);
            toggle();
        } catch (error) {
            console.error("Erreur lors de la réception:", error);
            alert("Échec de la réception, veuillez réessayer.");
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setQuantity(0);
        toggle();
    };

    return (
        <Modal isOpen={isOpen} toggle={handleClose}>
            <ModalHeader toggle={handleClose}>
                <i className="ri-archive-line me-2"></i>
                Réceptionner en stock
            </ModalHeader>
            <ModalBody>
                {orderLine && (
                    <div>
                        <div className="mb-3">
                            <h6>Commande fournisseur</h6>
                            <div className="small text-muted">
                                <strong>Réf.:</strong> {orderLine.supplier_order.ref}
                            </div>
                            <div className="small text-muted">
                                <strong>Fournisseur:</strong> {orderLine.supplier.nom}
                            </div>
                        </div>
                        <div className="mb-3">
                            <h6>Produit</h6>
                            <div className="small text-muted">
                                <strong>Réf.:</strong> {orderLine.product.ref}
                            </div>
                            <div className="small text-muted">
                                <strong>Libellé:</strong> {orderLine.product.label}
                            </div>
                        </div>
                        <div className="mb-3">
                            <h6>Quantités</h6>
                            <div className="d-flex justify-content-between mb-2">
                                <div className="text-center">
                                    <div className="fw-bold">{orderLine.quantities.ordered}</div>
                                    <small className="text-muted">Commandée</small>
                                </div>
                                <div className="text-center">
                                    <div className="fw-bold text-success">{orderLine.quantities.received}</div>
                                    <small className="text-muted">Déjà reçue</small>
                                </div>
                                <div className="text-center">
                                    <div className="fw-bold text-primary">{orderLine.quantities.remaining}</div>
                                    <small className="text-muted">Restante</small>
                                </div>
                            </div>
                        </div>
                        <div className="mb-3">
                            <Label for="receiveQuantity" className="form-label">
                                Quantité à réceptionner
                            </Label>
                            <Input
                                type="number"
                                id="receiveQuantity"
                                min="1"
                                max={orderLine.quantities.remaining}
                                value={quantity}
                                onChange={(e) => setQuantity(parseInt(e.target.value) || 0)}
                                disabled={loading}
                            />
                            <div className="form-text">
                                Maximum disponible : {orderLine.quantities.remaining}
                            </div>
                        </div>
                        <FormGroup className="mb-3">
                            <Label for="warehouse" className="form-label">
                                Entrepôt de réception
                            </Label>
                            <Input
                                type="select"
                                id="warehouse"
                                value={selectedWarehouseId}
                                onChange={(e) => onWarehouseChange(e.target.value)}
                                disabled={loading || warehouses.length === 0}
                            >
                                {warehouses.map((wh) => (
                                    <option key={wh.id} value={wh.id}>
                                        {wh.ref}
                                    </option>
                                ))}
                            </Input>
                            {warehouses.length === 0 && (
                                <div className="form-text text-danger">
                                    Aucun entrepôt disponible. Contactez l'administrateur.
                                </div>
                            )}
                        </FormGroup>
                    </div>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={handleClose} disabled={loading}>
                    Annuler
                </Button>
                <Button
                    color="primary"
                    onClick={handleSubmit}
                    disabled={
                        loading ||
                        !quantity ||
                        quantity > (orderLine?.quantities.remaining || 0) ||
                        !selectedWarehouseId ||
                        warehouses.length === 0
                    }
                >
                    {loading ? (
                        <>
                            <Spinner size="sm" className="me-2" />
                            Traitement...
                        </>
                    ) : (
                        <>
                            <i className="ri-check-line me-1"></i>
                            Réceptionner
                        </>
                    )}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

const OrdersToStockPage: React.FC = () => {
    const { user } = useAuth();
    const [orderLines, setOrderLines] = useState<SupplierOrderLine[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [countPerPage, setCountPerPage] = useState(0);

    const [searchSupplierOrderRef, setSearchSupplierOrderRef] = useState("");
    const [searchProductRef, setSearchProductRef] = useState("");
    const [searchSupplier, setSearchSupplier] = useState("");
    // Modification : par défaut, afficher les commandes non réceptionnées (statut = 0)
    const [selectedStatut, setSelectedStatut] = useState<string>("0");

    // États entrepôts (comme dans Stock)
    const [entrepots, setEntrepots] = useState<Entrepot[]>([]);
    const [selectedWarehouseId, setSelectedWarehouseId] = useState<string>("");
    const [loadingWarehouses, setLoadingWarehouses] = useState(false);

    const [selectedOrderLine, setSelectedOrderLine] = useState<SupplierOrderLine | null>(null);
    const [receiveModalOpen, setReceiveModalOpen] = useState(false);
    const navigate = useNavigate();

    
    const canReceive = user?.perms?.fournisseur?.commande?.receptionner === 1;

    
    const fetchWarehouses = async () => {
        setLoadingWarehouses(true);
        try {
            const response: any = await axios.post(
                "https://api.apapiecesauto.fr/apiV2/",
                {
                    action: "getDataFournisseurs",
                    entity: user.entity,
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${user.token}`,
                    },
                }
            );
            if (response && response.success) {
                const warehousesData = response.entrepots || [];
                setEntrepots(warehousesData);
                if (warehousesData.length > 0) {
                    setSelectedWarehouseId(warehousesData[0].id);
                }
            } else {
                console.error("Erreur chargement entrepôts:", response?.message);
            }
        } catch (error) {
            console.error("Erreur lors du chargement des entrepôts:", error);
        } finally {
            setLoadingWarehouses(false);
        }
    };

    const fetchOrdersToStock = async () => {
        setLoading(true);
        try {
            const searchParams: any = {};
            if (searchSupplierOrderRef) searchParams.supplier_order_ref = searchSupplierOrderRef;
            if (searchProductRef) searchParams.product_ref = searchProductRef;
            if (searchSupplier) searchParams.supplier_name = searchSupplier;

            const payload: any = {
                action: "orderstostock",
                entity: user.entity,
                page: page,
                limit: 500,
            };
            if (Object.keys(searchParams).length > 0) {
                payload.search = searchParams;
            }
            if (selectedStatut !== "") {
                payload.statuts = parseInt(selectedStatut);
            }

            const response: any = await axios.post(
                "https://api.apapiecesauto.fr/apiV2/",
                payload,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${user.token}`,
                    },
                }
            );

            let data: SupplierOrderLine[] = [];
            let totalRecords = 0;
            let totalPagesValue = 1;
            let currentCount = 0;

            if (response && response.data && Array.isArray(response.data)) {
                data = response.data;
                totalRecords = response.nbtotalofrecords || 0;
                totalPagesValue = response.totalpages || 1;
                currentCount = response.count || data.length;
            } else if (Array.isArray(response.data)) {
                data = response.data;
                currentCount = data.length;
                totalRecords = data.length;
                totalPagesValue = 1;
            }

            const sortedData = [...data].sort((a, b) => {
                const dateA = new Date(a.supplier_order.date).getTime();
                const dateB = new Date(b.supplier_order.date).getTime();
                return dateA - dateB;
            });

            setOrderLines(sortedData);
            setTotalCount(totalRecords);
            setTotalPages(totalPagesValue);
            setCountPerPage(currentCount);
        } catch (error) {
            console.error("Erreur lors du chargement des commandes stock:", error);
            setOrderLines([]);
            setTotalCount(0);
            setTotalPages(1);
            setCountPerPage(0);
        } finally {
            setLoading(false);
        }
    };

    // Réception via l'API setreception (mode existing uniquement)
    const handleReceiveStock = async (orderLine: SupplierOrderLine, quantity: number, warehouseId: string) => {
        try {
            const response: any = await axios.post(
                "https://api.apapiecesauto.fr/apiV2/",
                {
                    action: "setreception",
                    supplier_id: orderLine.supplier.id,
                    supplier_ref: orderLine.supplier_order.ref,
                    entity: user.entity,
                    warehouse_id: warehouseId,
                    lines: [
                        {
                            line_id: orderLine.supplier_order_line_id,
                            qty: quantity,
                        },
                    ],
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${user.token}`,
                    },
                }
            );

            if (response && response.success !== false) {
                await fetchOrdersToStock();
                return;
            }
            throw new Error(response?.message || "Échec de la réception");
        } catch (error) {
            console.error("Erreur lors de la réception:", error);
            throw error;
        }
    };

    const openReceiveModal = (orderLine: SupplierOrderLine) => {
        setSelectedOrderLine(orderLine);
        setReceiveModalOpen(true);
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setPage(1);
        fetchOrdersToStock();
    };

    const clearFilters = () => {
        setSearchSupplierOrderRef("");
        setSearchProductRef("");
        setSearchSupplier("");
        // Optionnel : si vous souhaitez que le bouton Effacer remette aussi le statut par défaut à "Non réceptionné"
        // setSelectedStatut("0");
        setSelectedStatut("");
        setPage(1);
        fetchOrdersToStock();
    };

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
        }
    };

    useEffect(() => {
        fetchOrdersToStock();
        fetchWarehouses();
    }, [page]);

    const SmartPagination = () => {
        const maxVisiblePages = 5;
        const halfVisible = Math.floor(maxVisiblePages / 2);
        const [pageInput, setPageInput] = useState(page.toString());

        useEffect(() => {
            setPageInput(page.toString());
        }, [page]);

        let startPage = Math.max(1, page - halfVisible);
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        const pages = [];
        for (let i = startPage; i <= endPage; i++) pages.push(i);

        const handlePageJump = () => {
            const newPage = parseInt(pageInput);
            if (!isNaN(newPage)) {
                const clampedPage = Math.max(1, Math.min(totalPages, newPage));
                setPage(clampedPage);
            }
        };

        const handleKeyDown = (e: React.KeyboardEvent) => {
            if (e.key === "Enter") handlePageJump();
        };

        return (
            <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="text-muted">
                    Affichage <strong>{countPerPage}</strong> ligne(s)
                </div>
                <ul className="pagination mb-0">
                    <li className={`page-item ${page === 1 || loading ? "disabled" : ""}`}>
                        <button className="page-link" onClick={() => handlePageChange(1)}>«</button>
                    </li>
                    <li className={`page-item ${page === 1 || loading ? "disabled" : ""}`}>
                        <button className="page-link" onClick={() => handlePageChange(page - 1)}>‹</button>
                    </li>
                    {startPage > 1 && (
                        <>
                            <li className="page-item"><button className="page-link" onClick={() => handlePageChange(1)}>1</button></li>
                            {startPage > 2 && <li className="page-item disabled"><span className="page-link">...</span></li>}
                        </>
                    )}
                    {pages.map(p => (
                        <li key={p} className={`page-item ${p === page ? "active" : ""}`}>
                            <button className="page-link" onClick={() => handlePageChange(p)}>{p}</button>
                        </li>
                    ))}
                    {endPage < totalPages && (
                        <>
                            {endPage < totalPages - 1 && <li className="page-item disabled"><span className="page-link">...</span></li>}
                            <li className="page-item"><button className="page-link" onClick={() => handlePageChange(totalPages)}>{totalPages}</button></li>
                        </>
                    )}
                    <li className={`page-item ${page === totalPages || loading ? "disabled" : ""}`}>
                        <button className="page-link" onClick={() => handlePageChange(page + 1)}>›</button>
                    </li>
                    <li className={`page-item ${page === totalPages || loading ? "disabled" : ""}`}>
                        <button className="page-link" onClick={() => handlePageChange(totalPages)}>»</button>
                    </li>
                </ul>
                <div className="d-flex align-items-center gap-1">
                    <Input
                        type="number"
                        min="1"
                        max={totalPages}
                        value={pageInput}
                        onChange={(e) => setPageInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        style={{ width: "70px" }}
                        className="text-center"
                        bsSize="sm"
                        disabled={loading}
                    />
                    <Button size="sm" color="primary" onClick={handlePageJump} disabled={loading}>
                        {loading ? <Spinner size="sm" /> : "Aller"}
                    </Button>
                </div>
            </div>
        );
    };

    const customStyles = `
        .compact-table-custom {
            width: 100%;
            border-collapse: collapse;
        }
        .compact-table-custom th,
        .compact-table-custom td {
            padding: 6px 8px !important;
            vertical-align: middle !important;
            border-bottom: 1px solid #e9ecef;
        }
        .compact-table-custom th {
            background-color: #f8f9fa;
            font-weight: 600;
            color: #495057;
        }
        .quantities-cell-custom {
            line-height: 1.3;
        }
        .quantities-cell-custom > div {
            display: flex;
            justify-content: space-between;
            margin-bottom: 2px;
        }
        .action-cell-custom {
            text-align: center;
        }
        .action-cell-custom button {
            margin: 0 auto;
        }
        @media (min-width: 1500px) {
            .compact-table-custom th,
            .compact-table-custom td {
                padding: 4px 6px !important;
            }
        }
    `;

    const getStatutInfo = (statut: number) => {
        switch (statut) {
            case 0:
                return { label: "Non réceptionné", color: "danger" };
            case 1:
                return { label: "Partiellement réceptionné", color: "warning" };
            case 2:
                return { label: "Réceptionné", color: "success" };
            default:
                return { label: "Inconnu", color: "secondary" };
        }
    };

    return (
        <div className="page-content">
            <style>{customStyles}</style>
            <Container fluid>
                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardBody>
                                <div className="d-flex justify-content-between mb-3">
                                    <h4 className="card-title mb-0">
                                        <i className="ri-stock-line align-bottom me-2"></i>
                                        Commandes Stock
                                    </h4>
                                    <div className="text-muted">
                                        Total : <strong>{totalCount}</strong> ligne(s) trouvée(s)
                                    </div>
                                </div>

                                <form onSubmit={handleSearch} className="mb-4">
                                    <Row className="g-3 align-items-end">
                                        <Col md={3}>
                                            <FormGroup>
                                                <Label className="form-label small text-muted mb-1">
                                                    RÉF. COMMANDE FOURNISSEUR
                                                </Label>
                                                <InputGroup>
                                                    <InputGroupText className="bg-light border-end-0">
                                                        <i className="ri-shopping-bag-line"></i>
                                                    </InputGroupText>
                                                    <Input
                                                        type="text"
                                                        placeholder="Réf. commande fournisseur..."
                                                        value={searchSupplierOrderRef}
                                                        onChange={(e) => setSearchSupplierOrderRef(e.target.value)}
                                                        className="border-start-0"
                                                    />
                                                </InputGroup>
                                            </FormGroup>
                                        </Col>
                                        <Col md={2}>
                                            <FormGroup>
                                                <Label className="form-label small text-muted mb-1">
                                                    RÉF. PRODUIT
                                                </Label>
                                                <InputGroup>
                                                    <InputGroupText className="bg-light border-end-0">
                                                        <i className="ri-barcode-line"></i>
                                                    </InputGroupText>
                                                    <Input
                                                        type="text"
                                                        placeholder="Référence produit..."
                                                        value={searchProductRef}
                                                        onChange={(e) => setSearchProductRef(e.target.value)}
                                                        className="border-start-0"
                                                    />
                                                </InputGroup>
                                            </FormGroup>
                                        </Col>
                                        <Col md={2}>
                                            <FormGroup>
                                                <Label className="form-label small text-muted mb-1">
                                                    FOURNISSEUR
                                                </Label>
                                                <InputGroup>
                                                    <InputGroupText className="bg-light border-end-0">
                                                        <i className="ri-store-line"></i>
                                                    </InputGroupText>
                                                    <Input
                                                        type="text"
                                                        placeholder="Nom du fournisseur..."
                                                        value={searchSupplier}
                                                        onChange={(e) => setSearchSupplier(e.target.value)}
                                                        className="border-start-0"
                                                    />
                                                </InputGroup>
                                            </FormGroup>
                                        </Col>
                                        <Col md={2}>
                                            <FormGroup>
                                                <Label className="form-label small text-muted mb-1">
                                                    STATUT
                                                </Label>
                                                <Input
                                                    type="select"
                                                    value={selectedStatut}
                                                    onChange={(e) => setSelectedStatut(e.target.value)}
                                                    className="form-select"
                                                >
                                                    <option value="">Tous</option>
                                                    <option value="0">Non réceptionné</option>
                                                    <option value="1">Partiellement réceptionné</option>
                                                    <option value="2">Réceptionné</option>
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                        <Col md={2} className="ms-auto">
                                            <FormGroup>
                                                <Label className="form-label small text-muted mb-1 d-block">&nbsp;</Label>
                                                <div className="d-flex gap-2">
                                                    <Button type="submit" color="primary" className="flex-grow-1" disabled={loading}>
                                                        {loading ? <Spinner size="sm" /> : <><i className="ri-search-line me-1"></i> Rechercher</>}
                                                    </Button>
                                                    <Button type="button" color="light" onClick={clearFilters} disabled={loading}>
                                                        <i className="ri-eraser-line"></i>
                                                    </Button>
                                                </div>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </form>

                                {loading && orderLines.length === 0 ? (
                                    <div className="text-center py-5">
                                        <Spinner color="primary" />
                                        <div className="mt-2">Chargement des commandes...</div>
                                    </div>
                                ) : orderLines.length === 0 ? (
                                    <div className="text-center py-5">
                                        <i className="ri-inbox-line display-4 text-muted mb-3"></i>
                                        <h5 className="mb-2">Aucune commande d'approvisionnement</h5>
                                        <p className="text-muted mb-4">
                                            Aucune ligne de commande fournisseur trouvée.
                                        </p>
                                        <Button color="primary" onClick={clearFilters} className="d-flex align-items-center mx-auto">
                                            <i className="ri-refresh-line me-2"></i> Actualiser
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="compact-table-custom">
                                            <thead>
                                                <tr>
                                                    <th>Commande fournisseur</th>
                                                    <th>Fournisseur</th>
                                                    <th>Produit</th>
                                                    <th>Date</th>
                                                    <th>Quantités</th>
                                                    <th>Total HT</th>
                                                    <th>Statut</th>
                                                    {canReceive && <th>Action</th>}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {orderLines.map((line) => {
                                                    const statutInfo = getStatutInfo(line.statut);
                                                    return (
                                                        <tr key={line.supplier_order_line_id}>
                                                            <td className="fw-semibold">{line.supplier_order.ref}</td>
                                                            <td className="fw-semibold">{line.supplier.nom}</td>
                                                            <td>
                                                                <div className="fw-semibold">{line.product.ref}</div>
                                                                <small className="text-muted d-block text-truncate" style={{ maxWidth: "220px" }}>
                                                                    {line.product.label}
                                                                </small>
                                                            </td>
                                                            <td>
                                                                {new Date(line.supplier_order.date).toLocaleDateString('fr-FR', {
                                                                    day: 'numeric',
                                                                    month: 'long',
                                                                    year: 'numeric'
                                                                })}
                                                            </td>
                                                            <td className="quantities-cell-custom">
                                                                <div>
                                                                    <span className="text-muted">Commandée :</span>
                                                                    <span className="fw-semibold" style={{paddingRight:"95px"}}>{line.quantities.ordered}</span>
                                                                </div>
                                                                <div>
                                                                    <span className="text-muted">Reçue :</span>
                                                                    <span className="fw-semibold text-success" style={{paddingRight:"95px"}}>{line.quantities.received}</span>
                                                                </div>
                                                                <div>
                                                                    <span className="text-muted">Restante :</span>
                                                                    <span className="fw-semibold text-primary" style={{paddingRight:"95px"}}>{line.quantities.remaining}</span>
                                                                </div>
                                                            </td>
                                                            <td>{parseFloat(line.line_details.total_ht.toString()).toFixed(2)} €</td>
                                                            <td>
                                                                <span className={`badge bg-${statutInfo.color}`}>
                                                                    {statutInfo.label}
                                                                </span>
                                                            </td>
                                                            {canReceive && (
                                                                <td className="action-cell-custom">
                                                                    <Button
                                                                        color={line.quantities.remaining > 0 ? "success" : "secondary"}
                                                                        size="sm"
                                                                        onClick={() => line.quantities.remaining > 0 && openReceiveModal(line)}
                                                                        disabled={line.quantities.remaining === 0 || loading}
                                                                        className="d-flex align-items-center mx-auto"
                                                                    >
                                                                        <i className="ri-archive-line me-1"></i>
                                                                        Réceptionner
                                                                    </Button>
                                                                </td>
                                                            )}
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                        {totalPages > 1 && <SmartPagination />}
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {canReceive && (
                <ReceiveModal
                    isOpen={receiveModalOpen}
                    toggle={() => setReceiveModalOpen(false)}
                    orderLine={selectedOrderLine}
                    onReceive={handleReceiveStock}
                    warehouses={entrepots}
                    selectedWarehouseId={selectedWarehouseId}
                    onWarehouseChange={setSelectedWarehouseId}
                />
            )}
        </div>
    );
};

export default OrdersToStockPage;